import React, { useEffect, useState } from "react";
import {
  HandHeart,
  IndianRupee,
  Package,
  ClipboardList,
  User,
  Phone as PhoneIcon,
} from "lucide-react";
import Select from "react-select";
import { toast } from "react-toastify";
import { db } from "../../config/firebase";
import { collection, addDoc, getDocs, Timestamp } from "firebase/firestore";
import { QrCode, CreditCard } from "lucide-react";




const DonationModal = () => {
  const [donationType, setDonationType] = useState("");
  const [utilities, setUtilities] = useState([]);
  const [selectedUtility, setSelectedUtility] = useState(null);
  const [donationQuantity, setDonationQuantity] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorPhone, setDonorPhone] = useState("");
  const [selectedPayment, setSelectedPayment] = useState("upi");

  // Fetch utilities from Firestore
  useEffect(() => {
    const fetchUtilities = async () => {
      try {
        const snapshot = await getDocs(collection(db, "utilityItems"));
        const utilityList = snapshot.docs.map((doc) => doc.data());
        setUtilities(utilityList);
      } catch (err) {
        console.error("Error fetching utilities:", err);
      }
    };
    fetchUtilities();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(donorPhone)) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    const donationDetails = {
      item: selectedUtility?.value || "",
      quantity: donationQuantity,
      name: donorName,
      phone: donorPhone,
      timestamp: Timestamp.now(),
      status: "pending",
    };

    try {
      await addDoc(collection(db, "donationSubmissions"), donationDetails);
      toast.success("Thank you for your donation! We'll contact you soon.");
      setSelectedUtility(null);
      setDonationQuantity("");
      setDonorName("");
      setDonorPhone("");
      setDonationType("");
    } catch (err) {
      toast.error("Error submitting donation. Try again.");
      console.error(err);
    }
  };

  const utilityOptions = utilities.map((u) => {
    const qty = String(u.quantityNeeded || "").trim();
    return {
      value: u.item,
      label: qty ? `${u.item} (${qty})` : u.item,
    };
  });

  return (
    <section className="max-w-4xl mx-auto px-6 py-20">
      <h2 className="text-4xl font-semibold font-poppins text-gray-800 mb-8 flex items-center gap-3">
        <HandHeart className="text-green-700" size={32} />
        Donate to Support
      </h2>

      {/* Donation Type Dropdown */}
      <div className="mb-6">
        <label className="block mb-2 text-gray-700 font-medium text-sm">
          Donation Type
        </label>
        <Select
          value={
            donationType ? { label: donationType, value: donationType } : null
          }
          onChange={(selected) => setDonationType(selected?.value || "")}
          options={[
            { value: "Donate Utilities", label: "Donate utilities" },
            { value: "Donate Money", label: "Donate money" },
          ]}
          placeholder="Select donation type"
          classNamePrefix="react-select"
          isSearchable={false}
          styles={{
            control: (base) => ({
              ...base,
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              fontSize: "0.875rem",
              minHeight: "42px",
              boxShadow: "none",
              "&:hover": { borderColor: "#d1d5db" },
            }),
          }}
        />
      </div>

      {/* Donate Money Section */}
      {donationType === "Donate Money" && (
        <div className="mt-6 bg-green-50 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
            <IndianRupee className="text-green-700" size={22} />
            Choose Payment Method
          </h3>

          {/* Clean Icon Tabs */}
          <div className="flex flex-wrap justify-center sm:justify-start gap-3 mb-6">
            {[
              {
                key: "upi",
                label: "Via UPI",
                icon: <QrCode size={16} className="mr-1.5" />,
              },
              {
                key: "razorpay",
                label: "Via Razorpay",
                icon: <CreditCard size={16} className="mr-1.5" />,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSelectedPayment(tab.key)}
                className={`flex items-center justify-center px-4 py-2 rounded-md border text-sm font-medium transition-all duration-200
            ${
              selectedPayment === tab.key
                ? "bg-green-600 text-white border-green-600"
                : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
            }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Render Based on Selection */}
          <div className="flex justify-center">
            {selectedPayment === "upi" && (
              <div className="flex flex-col items-center text-center bg-white border border-green-100 rounded-lg p-6 transition-all duration-300 w-full max-w-sm mx-auto">
                {/* QR Code */}
                <img
                  src="/other/qr-donation.png"
                  alt="UPI QR Code"
                  className="w-44 h-44 mb-4"
                />

                {/* Text for Desktop only */}
                <div className="hidden sm:block">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    Scan this QR to donate via any UPI app
                  </p>
                  <p className="text-xs text-gray-500">
                    Works with GPay, PhonePe, Paytm, BHIM, etc.
                  </p>
                </div>

                {/* Mobile-only Text + Button */}
                <div className="sm:hidden flex flex-col items-center">
                  <p className="text-sm text-gray-700 font-medium mb-3">
                    Tap below to donate instantly — no need to scan!
                  </p>

                  <button
                    onClick={() =>
                      (window.location.href =
                        "upi://pay?pa=MAB.037326005710026@axisbank&pn=Diksha%20Foundation&cu=INR&tn=Donation%20to%20Diksha%20Foundation")
                    }
                    className="bg-green-600 animate-pulse text-white text-sm font-medium px-6 py-2 rounded-md shadow-sm transition-all duration-200"
                  >
                    Pay via UPI App
                  </button>
                </div>
              </div>
            )}

            {selectedPayment === "razorpay" && (
              <div className="flex flex-col items-center text-center bg-white border border-green-100 rounded-lg p-6 transition-all duration-300 w-full max-w-sm">
                <p className="text-sm text-gray-700 mb-3">
                  You’ll be redirected to our secure Razorpay donation page.
                </p>
                <button
                  onClick={() =>
                    window.open(
                      "https://razorpay.me/@dikshafoundation",
                      "_blank"
                    )
                  }
                  className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-6 py-2 rounded-md shadow-sm transition"
                >
                  Donate via Razorpay
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Utilities Donation Form */}
      {donationType === "Donate Utilities" && (
        <form
          onSubmit={handleSubmit}
          className="mt-10 bg-green-50 p-6 rounded-xl shadow-sm"
        >
          <h3 className="text-xl font-semibold mb-4 text-green-900">
            Donate Required Items
          </h3>

          {/* Item Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Item to donate
            </label>
            <div className="flex items-center gap-2">
              <Package className="text-green-700" size={18} />
              <div className="w-full">
                <Select
                  value={selectedUtility}
                  onChange={setSelectedUtility}
                  options={utilityOptions}
                  placeholder="Select item"
                  classNamePrefix="react-select"
                  isSearchable={false}
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      fontSize: "0.875rem",
                      minHeight: "42px",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#d1d5db" },
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Quantity Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Quantity you can donate
            </label>
            <div className="flex items-center gap-2">
              <ClipboardList className="text-green-700" size={18} />
              <input
                type="text"
                value={donationQuantity}
                onChange={(e) => setDonationQuantity(e.target.value)}
                required
                className="w-full border border-gray-300 rounded px-4 py-2 text-sm outline-green-500"
                placeholder="e.g. 5 packs"
              />
            </div>
          </div>

          {/* Name Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Your Name
            </label>
            <div className="flex items-center gap-2">
              <User className="text-green-700" size={18} />
              <input
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                required
                className="w-full border border-gray-300 rounded px-4 py-2 text-sm outline-green-500"
                placeholder="Full Name"
              />
            </div>
          </div>

          {/* Phone Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Contact Number
            </label>
            <div className="flex items-center gap-2">
              <PhoneIcon className="text-green-700" size={18} />
              <input
                type="tel"
                value={donorPhone}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  if (digitsOnly.length <= 10) {
                    setDonorPhone(digitsOnly);
                  }
                }}
                required
                maxLength={10}
                inputMode="numeric"
                pattern="\d{10}"
                className="w-full border border-gray-300 rounded px-4 py-2 text-sm outline-green-500"
                placeholder="Mobile Number"
              />
            </div>
          </div>

          <button
            type="submit"
            className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
          >
            Submit
          </button>
        </form>
      )}
    </section>
  );
};

export default DonationModal;
